import React, { useState, useEffect, useCallback, useRef } from "react";
import AgentCard from "../AgentDashboard/AgentCard";
import CreateNewAgentCard from "../AgentDashboard/CreateNewAgentCard";
import { deleteAgent, fetchAgentsByCustomer } from "../../../services/agentService";
import { fetchConversationThread } from "../../../services/conversationHistoryService";
import ChatWithSidebar from "./ChatWithSidebar";
import ConfirmDialog from "../../Shared/ConfirmDialog";
import { useToast } from "../../../context/ToastContext";
import { useAuth } from "../../../context/AuthContext";
import { useCustomerWorkspace } from "../../../context/CustomerWorkspaceContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import { UIAgent } from "../../../types/Agent";
import { useBrandStatus } from "../../../features/brand/hooks/useBrandStatus";
import { debug } from "../../../utils/debugLogger";
import { pickAgentTypeFromApi, isCommunicationAgentType } from "../../../constants/agentTypes";

function mapApiAgentToUi(agent: any): UIAgent {
  let tools: string[] = [];
  if (agent.tools) {
    if (Array.isArray(agent.tools)) {
      tools = agent.tools
        .map((tool: any) => {
          if (typeof tool === "string") return tool;
          if (tool && typeof tool === "object" && tool.name) return tool.name;
          return null;
        })
        .filter((t: string | null) => t !== null) as string[];
    } else if (typeof agent.tools === "object") {
      tools = Object.keys(agent.tools).filter((key) => agent.tools[key] === true);
    }
  }
  const agentType = pickAgentTypeFromApi(agent) || "content_creator";

  // Mirror the backend tool_registry: content_creator agents always support
  // output_social_posts even if the legacy DynamoDB record has an empty tools array.
  if (agentType === "content_creator" && !tools.includes("output_social_posts")) {
    tools = ["output_social_posts", ...tools];
  }

  // Likewise, content_creator agents support output_blog (the Blog chip / blog SSE
  // flow). Surface the chip even when the legacy DynamoDB record omits the tool.
  if (agentType === "content_creator" && !tools.includes("output_blog")) {
    tools = ["output_blog", ...tools];
  }

  // Outreach Email chip / outreach_email SSE flow (SGL-83) — AI Receptionist only
  // (communication_agent_email / _sms). Its epic, SGL-78 "Bulk Contact Import &
  // Personalized Outreach Campaigns (AI Receptionist)", is the receptionist's
  // outreach workflow, so the chip does not belong on content/social agents.
  // Mirrors the backend's own gating in tool_registry.get_tools_for_agent.
  if (isCommunicationAgentType(agentType) && !tools.includes("output_outreach_email")) {
    tools = ["output_outreach_email", ...tools];
  }

  return {
    id: agent.agent_id || agent.aiagent_id,
    name: agent.agent_name || "Unnamed Agent",
    avatar:
      agent.profile_picture_url && agent.profile_picture_url !== ""
        ? agent.profile_picture_url
        : "/avatars/default-avatar.png",
    conversation_starters: agent.conversation_starters || [],
    model: agent.model || undefined,
    vector_store_id: agent.vector_store_id || undefined,
    tools: tools.length > 0 ? tools : undefined,
    agent_type: agentType,
    provider: agent.provider || undefined,
    updated_at: agent.updated_at || agent.last_updated || undefined,
    status: agent.status || undefined,
  };
}

const ChatWithAgents: React.FC = () => {
  const { idToken } = useAuth();
  const { selectedCustomerId, setSelectedCustomerId } = useCustomerWorkspace();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const brandStatus = useBrandStatus(selectedCustomerId, idToken || null);
  const { showToast } = useToast();

  // Delete-agent confirmation. Mirrors the media-delete flow in DisplayMedia:
  // the pending target is held in state while the dialog is open, and the
  // error surfaces inside the dialog rather than dismissing it.
  const [pendingDeleteAgent, setPendingDeleteAgent] = useState<UIAgent | null>(null);
  const [isDeletingAgent, setIsDeletingAgent] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const agentIdFromUrl = searchParams.get("agentId");
  const customerIdFromUrl = searchParams.get("customerId");
  const threadIdFromUrl = searchParams.get("threadId");

  const handleThreadIdChange = useCallback(
    (tid: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tid) {
            next.set("threadId", tid);
          } else {
            next.delete("threadId");
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [agents, setAgents] = useState<UIAgent[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentsError, setAgentsError] = useState<string | null>(null);
  const prevCustomerRef = useRef<string | null>(null);

  /**
   * Deep-link hydration from URL should only run when workspace context
   * does not already have a selected business. This avoids stale query params
   * overriding a newer selection made from the sidebar on other pages.
   */
  useEffect(() => {
    if (selectedCustomerId) return;
    const customerId = searchParams.get("customerId");
    if (customerId) {
      setSelectedCustomerId(customerId);
    }
  }, [searchParams, selectedCustomerId, setSelectedCustomerId]);

  /**
   * Align customerId in the URL with workspace. When the business actually changes,
   * drop agent/thread; when only adding customerId (or same business), keep deep link.
   */
  const urlCustomerFromSearch = searchParams.get("customerId");
  useEffect(() => {
    if (!selectedCustomerId) return;
    if (urlCustomerFromSearch === selectedCustomerId) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const prevCust = prev.get("customerId");
        next.set("customerId", selectedCustomerId);
        if (prevCust && prevCust !== selectedCustomerId) {
          next.delete("agentId");
          next.delete("threadId");
        }
        return next;
      },
      { replace: true }
    );
  }, [selectedCustomerId, urlCustomerFromSearch, setSearchParams]);

  /** When business changes, drop agent/thread from URL (source of truth). */
  useEffect(() => {
    if (
      prevCustomerRef.current !== null &&
      prevCustomerRef.current !== selectedCustomerId
    ) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("agentId");
          next.delete("threadId");
          if (selectedCustomerId) {
            next.set("customerId", selectedCustomerId);
          } else {
            next.delete("customerId");
          }
          return next;
        },
        { replace: true }
      );
    }
    prevCustomerRef.current = selectedCustomerId;
  }, [selectedCustomerId, setSearchParams]);

  /**
   * Deep link has threadId but no agentId (e.g. older clients). Resolve agent from API
   * so showChatShell can open once brand is valid.
   */
  const lastAgentResolveThreadRef = useRef<string | null>(null);
  useEffect(() => {
    if (agentIdFromUrl) {
      lastAgentResolveThreadRef.current = null;
      return;
    }
    if (
      !threadIdFromUrl ||
      !idToken ||
      brandStatus !== "valid" ||
      !selectedCustomerId
    ) {
      return;
    }
    if (lastAgentResolveThreadRef.current === threadIdFromUrl) return;
    lastAgentResolveThreadRef.current = threadIdFromUrl;
    let cancelled = false;
    fetchConversationThread(threadIdFromUrl, idToken)
      .then((t) => {
        if (cancelled) return;
        if (!t.agentId) {
          lastAgentResolveThreadRef.current = null;
          return;
        }
        setSearchParams(
          (prev) => {
            if (prev.get("threadId") !== threadIdFromUrl) return prev;
            if (prev.get("agentId")) return prev;
            const next = new URLSearchParams(prev);
            next.set("agentId", t.agentId!);
            return next;
          },
          { replace: true }
        );
      })
      .catch(() => {
        lastAgentResolveThreadRef.current = null;
      });
    return () => {
      cancelled = true;
    };
  }, [
    threadIdFromUrl,
    agentIdFromUrl,
    idToken,
    brandStatus,
    selectedCustomerId,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!selectedCustomerId || !idToken) {
      setAgents([]);
      setLoadingAgents(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingAgents(true);
      setAgentsError(null);
      try {
        const fetched = await fetchAgentsByCustomer(selectedCustomerId, idToken);
        if (cancelled) return;
        // The backend already excludes deleted agents; this is belt-and-braces
        // against a stale cached response resurrecting one on the board.
        const mapped = (fetched as any[])
          .map(mapApiAgentToUi)
          .filter((agent) => agent.status !== "DEACTIVATED");
        mapped.sort((a, b) => a.name.localeCompare(b.name));
        setAgents(mapped);
      } catch (error) {
        debug.error("Error fetching agents for customer:", error);
        if (!cancelled) {
          setAgentsError("Failed to fetch agents for this business");
          setAgents([]);
        }
      } finally {
        if (!cancelled) setLoadingAgents(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedCustomerId, idToken]);

  const activeAgent =
    agentIdFromUrl && agents.length > 0
      ? agents.find((a) => a.id === agentIdFromUrl)
      : undefined;
  const availableAgents = agents.map((agent) => ({
    id: agent.id,
    name: agent.name,
    // SGL-328: the Visibility Manager's card hands work to the Receptionist and the
    // Social Media Manager by the names the owner gave them.
    agentType: agent.agent_type,
    avatar:
      agent.avatar && agent.avatar !== "/avatars/default-avatar.png"
        ? agent.avatar
        : undefined,
  }));

  const handleAgentClick = (agentId: string) => {
    if (!selectedCustomerId || agentId === agentIdFromUrl) return;
    setAgentsError(null);
    setSearchParams(
      { agentId, customerId: selectedCustomerId },
      { replace: true }
    );
  };

  const handleAgentAction = (agentId: string, action: string) => {
    switch (action) {
      case "chat":
        if (!selectedCustomerId) {
          setAgentsError("Choose a business profile in the sidebar first.");
          return;
        }
        if (brandStatus === "missing") {
          setAgentsError(
            "Complete brand details for this business before chatting."
          );
          return;
        }
        if (brandStatus === "loading") return;
        handleAgentClick(agentId);
        break;
      case "edit":
        navigate(`/edit-agent?agentId=${agentId}`);
        break;
      case "clone":
        if (!selectedCustomerId) {
          setAgentsError("Choose a business profile in the sidebar first.");
          return;
        }
        navigate(`/clone-agent?cloneFromAgentId=${agentId}&customerId=${selectedCustomerId}`);
        break;
      case "delete": {
        if (!selectedCustomerId) {
          setAgentsError("Choose a business profile in the sidebar first.");
          return;
        }
        const target = agents.find((a) => a.id === agentId);
        if (!target) return;
        setDeleteError(null);
        setPendingDeleteAgent(target);
        break;
      }
      case "view":
        debug.log("View agent details:", agentId);
        break;
      case "activate":
        debug.log("Activate agent:", agentId);
        break;
      default:
        break;
    }
  };

  const handleConfirmDeleteAgent = async () => {
    if (!pendingDeleteAgent || !selectedCustomerId || !idToken) return;

    const { id: deletedId, name: deletedName } = pendingDeleteAgent;
    setIsDeletingAgent(true);
    setDeleteError(null);
    try {
      await deleteAgent(deletedId, selectedCustomerId, idToken);

      // The agent is hidden server-side the moment this resolves (the file
      // purge continues in the background), so drop it from the board now
      // rather than refetching.
      setAgents((prev) => prev.filter((a) => a.id !== deletedId));
      setPendingDeleteAgent(null);

      // If the deleted agent is the one currently open in chat, leave that
      // view — it would otherwise render against an agent that no longer exists.
      if (agentIdFromUrl === deletedId) {
        setSearchParams({ customerId: selectedCustomerId }, { replace: true });
      }

      showToast(`"${deletedName}" was deleted.`, "success");
    } catch (error) {
      debug.error("Error deleting agent:", error);
      setDeleteError(
        error instanceof Error ? error.message : "Failed to delete agent."
      );
    } finally {
      setIsDeletingAgent(false);
    }
  };

  /** Return to agent picker: clear agent + thread in URL; keep business when known. */
  const handleChatClose = useCallback(() => {
    if (selectedCustomerId) {
      setSearchParams({ customerId: selectedCustomerId }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [setSearchParams, selectedCustomerId]);

  const showChatShell =
    Boolean(agentIdFromUrl) &&
    Boolean(customerIdFromUrl) &&
    Boolean(selectedCustomerId) &&
    brandStatus === "valid";

  const brandBlockingChat =
    Boolean(agentIdFromUrl) &&
    Boolean(selectedCustomerId) &&
    brandStatus === "loading";

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-zinc-50">
      <div className="relative min-h-0 flex-1 overflow-hidden">
        {brandBlockingChat ? (
          <div className="flex h-full items-center justify-center text-zinc-600">
            <div className="max-w-md text-center">
              <div className="relative mx-auto mb-6 h-16 w-16">
                <div className="absolute inset-0 animate-spin rounded-full border-4 border-brand-primary/30 border-t-brand-primary" />
              </div>
              <p className="mb-2 text-lg font-semibold text-zinc-900">
                Loading…
              </p>
              <p className="text-sm text-zinc-600">Checking your profile</p>
            </div>
          </div>
        ) : showChatShell && !loadingAgents && activeAgent && selectedCustomerId ? (
          <ChatWithSidebar
            idToken={idToken || ""}
            agentName={activeAgent.name}
            agentAvatar={
              activeAgent.avatar &&
              activeAgent.avatar !== "/avatars/default-avatar.png"
                ? activeAgent.avatar
                : undefined
            }
            agentId={agentIdFromUrl!}
            customerId={selectedCustomerId}
            model={activeAgent.model}
            vectorStoreId={activeAgent.vector_store_id}
            conversationStarters={activeAgent.conversation_starters || []}
            agentType={activeAgent.agent_type}
            tools={activeAgent.tools || []}
            onClose={handleChatClose}
            threadId={threadIdFromUrl}
            onThreadIdChange={handleThreadIdChange}
            availableAgents={availableAgents}
            onAgentChange={handleAgentClick}
          />
        ) : showChatShell && !loadingAgents && !activeAgent ? (
          <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
            <p className="text-zinc-700">This agent is no longer available.</p>
            <button
              type="button"
              onClick={handleChatClose}
              className="rounded-lg bg-brand-primary px-4 py-2 text-sm font-medium text-white hover:bg-brand-primary-hover"
            >
              Back to agents
            </button>
          </div>
        ) : showChatShell && loadingAgents ? (
          <div className="flex h-full items-center justify-center text-theme-fg-muted">
            <div className="max-w-md text-center">
              <div className="relative mx-auto mb-6 h-16 w-16">
                <div className="absolute inset-0 animate-spin rounded-full border-4 border-brand-primary/30 border-t-brand-primary" />
              </div>
              <p className="mb-2 text-lg font-semibold text-theme-fg">
                Opening chat…
              </p>
              <p className="text-sm text-theme-fg-muted">Loading your agents</p>
            </div>
          </div>
        ) : (
          <div className="h-full overflow-y-auto p-6 sm:p-8">
            <div className="mx-auto w-full max-w-6xl">
              {loadingAgents ? (
                <div className="flex min-h-[50vh] flex-col items-center justify-center">
                  <div className="relative mb-6 h-16 w-16">
                    <div className="absolute inset-0 animate-spin rounded-full border-4 border-brand-primary/30 border-t-brand-primary" />
                  </div>
                  <p className="text-lg font-semibold text-zinc-900">
                    Loading agents…
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-10 text-center">
                    <h1 className="mb-2 text-2xl font-semibold text-theme-fg">
                      Your agents
                    </h1>
                    <p className="text-sm text-theme-fg-muted">
                      Pick a business profile in the sidebar, then select an
                      agent to chat or manage.
                    </p>
                  </div>

                  {brandStatus === "missing" && selectedCustomerId && (
                    <div className="chat-brand-missing-banner mb-8 rounded-xl border border-[#c9a86c]/45 bg-[#fadfba]/80 p-4 shadow-sm ring-1 ring-amber-900/10 backdrop-blur-[2px]">
                      <p className="mb-3 text-sm text-theme-fg">
                        This business profile needs brand details. Complete them
                        or pick another profile in the sidebar.
                      </p>
                      {(threadIdFromUrl || agentIdFromUrl) && (
                        <p className="mb-3 text-sm text-theme-fg-muted">
                          A chat link is open in the address bar (recent thread).
                          After brand details are complete, that conversation will
                          open automatically.
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            `/business-profile/complete/${selectedCustomerId}`
                          )
                        }
                        className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700"
                      >
                        Set up brand details
                      </button>
                    </div>
                  )}

                  <div
                    className={
                      brandStatus === "missing"
                        ? "pointer-events-none opacity-60 transition-opacity"
                        : undefined
                    }
                    aria-disabled={brandStatus === "missing"}
                  >
                    {selectedCustomerId && !agentsError && (
                      <>
                        {agents.length > 0 && (
                          <div className="mb-6 flex items-center justify-between px-1">
                            <h2 className="flex items-center gap-2 text-lg font-semibold text-theme-fg">
                              Available agents
                              <span className="rounded-full border border-theme-border bg-theme-border/40 px-2.5 py-0.5 text-sm font-semibold text-theme-fg">
                                {agents.length}
                              </span>
                            </h2>
                          </div>
                        )}

                        {agents.length > 0 ? (
                          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                            {agents.map((agent, index) => (
                              <AgentCard
                                key={agent.id || `agent-${index}`}
                                agent={agent}
                                onAction={handleAgentAction}
                              />
                            ))}
                            <CreateNewAgentCard />
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                            <CreateNewAgentCard />
                          </div>
                        )}
                      </>
                    )}

                    {!selectedCustomerId && (
                      <p className="text-center text-sm text-theme-fg-muted">
                        Select a business profile in the left sidebar to see
                        agents.
                      </p>
                    )}
                  </div>

                  {agentsError && (
                    <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4">
                      <p className="mb-1 font-medium text-red-700">
                        Couldn&apos;t load agents
                      </p>
                      <p className="text-sm text-red-600">{agentsError}</p>
                    </div>
                  )}

                  {/*
                    Message is deliberately generic. Itemising what gets destroyed
                    invites the reader to audit the list — and "chat memory" in
                    particular reads as "my conversation history", which is NOT deleted.
                  */}
                  <ConfirmDialog
                    isOpen={pendingDeleteAgent !== null}
                    title="Delete agent?"
                    message={
                      <>
                        <p className="mb-2">
                          <strong>{pendingDeleteAgent?.name}</strong> and its data will be
                          permanently deleted.
                        </p>
                        <p>This can&apos;t be undone.</p>
                      </>
                    }
                    confirmLabel={isDeletingAgent ? "Deleting..." : "Delete"}
                    isConfirming={isDeletingAgent}
                    destructive
                    errorMessage={deleteError}
                    onConfirm={handleConfirmDeleteAgent}
                    onCancel={() => {
                      setPendingDeleteAgent(null);
                      setDeleteError(null);
                    }}
                  />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatWithAgents;
